import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, ArrowDownUp, Check, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { v4 as uuidv4 } from "uuid";
import NavBar from "../components/NavBar";

const currencySymbols = {
  JMD: "J$",
  USD: "$",
  GBP: "£",
  CAD: "C$"
};

const Exchange = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("JMD");
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [step, setStep] = useState(1);
  const [conversionResult, setConversionResult] = useState(null);

  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  const quoteMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/fx/quote`, {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        amount: parseFloat(amount)
      });
      return response.data;
    },
    onSuccess: (data) => {
      setQuote(data);
      setStep(2);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to get quote");
    }
  });

  const convertMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/fx/convert`, {
        quote_id: quote.quote_id,
        idempotency_key: uuidv4()
      });
      return response.data;
    },
    onSuccess: (data) => {
      setConversionResult(data);
      setStep(3);
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["transactions"]);
      toast.success("Conversion successful!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Conversion failed");
    }
  });

  const fromWallet = wallets?.find(w => w.currency === fromCurrency);

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setQuote(null);
    setStep(1);
  };

  const getQuote = () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (fromWallet && parseFloat(amount) > fromWallet.available_balance) {
      toast.error("Insufficient funds");
      return;
    }
    quoteMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="exchange-page">
      <header className="px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => step > 1 && step < 3 ? setStep(step - 1) : navigate("/dashboard")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">
          {step === 3 ? "Exchange Complete" : "Currency Exchange"}
        </h1>
      </header>

      <main className="px-6">
        {/* Step 1: Enter amount */}
        {step === 1 && (
          <div className="animate-slide-up">
            <div className="glass rounded-3xl p-6 mb-6">
              {/* From Currency */}
              <div className="mb-4">
                <label className="text-xs uppercase tracking-widest text-muted-foreground/70 block mb-2">
                  From
                </label>
                <div className="flex gap-3">
                  <select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-foreground flex-1"
                    data-testid="from-currency-select"
                  >
                    {["JMD", "USD", "GBP", "CAD"].filter(c => c !== toCurrency).map(c => (
                      <option key={c} value={c} className="bg-card">{c}</option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    placeholder="0.00"
                    className="input-glass flex-[2] font-mono-numbers text-xl"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="amount-input"
                  />
                </div>
                {fromWallet && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Available: {currencySymbols[fromCurrency]}{fromWallet.available_balance.toLocaleString()}
                  </p>
                )}
              </div>

              {/* Swap button */}
              <div className="flex justify-center my-4">
                <button
                  onClick={swapCurrencies}
                  className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors"
                  data-testid="swap-btn"
                >
                  <ArrowDownUp className="w-5 h-5" />
                </button>
              </div>

              {/* To Currency */}
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground/70 block mb-2">
                  To
                </label>
                <select
                  value={toCurrency}
                  onChange={(e) => setToCurrency(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-foreground"
                  data-testid="to-currency-select"
                >
                  {["JMD", "USD", "GBP", "CAD"].filter(c => c !== fromCurrency).map(c => (
                    <option key={c} value={c} className="bg-card">{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              className="w-full btn-primary h-14 text-base"
              onClick={getQuote}
              disabled={quoteMutation.isPending}
              data-testid="get-quote-btn"
            >
              {quoteMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <RefreshCcw className="w-5 h-5 animate-spin" />
                  Getting Quote...
                </span>
              ) : (
                "Get Quote"
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Confirm quote */}
        {step === 2 && quote && (
          <div className="animate-slide-up">
            <div className="glass rounded-3xl p-6 mb-6">
              <h2 className="font-heading font-medium text-lg text-center mb-6">
                Exchange Rate
              </h2>

              <div className="text-center mb-6">
                <p className="text-3xl font-mono-numbers font-bold text-foreground mb-2">
                  {currencySymbols[quote.from_currency]}{quote.from_amount.toLocaleString()}
                </p>
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <ArrowDownUp className="w-4 h-4" />
                  <span className="text-sm">1 {quote.from_currency} = {quote.rate} {quote.to_currency}</span>
                </div>
                <p className="text-3xl font-mono-numbers font-bold text-primary mt-4">
                  {currencySymbols[quote.to_currency]}{quote.to_amount.toLocaleString()}
                </p>
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Fee (2%)</span>
                  <span className="font-mono-numbers">
                    {currencySymbols[quote.from_currency]}{quote.fee.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Expires in</span>
                  <span className="text-yellow-400">5 minutes</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 btn-secondary h-14"
                onClick={() => { setStep(1); setQuote(null); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 btn-primary h-14"
                onClick={() => convertMutation.mutate()}
                disabled={convertMutation.isPending}
                data-testid="confirm-exchange-btn"
              >
                {convertMutation.isPending ? "Converting..." : "Confirm Exchange"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && conversionResult && (
          <div className="animate-slide-up text-center">
            <div className="glass rounded-3xl p-8 mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <Check className="w-10 h-10 text-primary" />
              </div>

              <h2 className="font-heading font-bold text-2xl mb-2">Exchange Complete!</h2>
              <p className="text-muted-foreground mb-6">
                Your currency has been converted
              </p>

              <div className="space-y-3">
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">From</span>
                  <span className="font-mono-numbers font-medium">
                    {currencySymbols[conversionResult.from_currency]}{conversionResult.from_amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">To</span>
                  <span className="font-mono-numbers font-medium text-primary">
                    {currencySymbols[conversionResult.to_currency]}{conversionResult.to_amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-muted-foreground">Rate</span>
                  <span className="font-mono-numbers">{conversionResult.rate}</span>
                </div>
              </div>
            </div>

            <Button
              className="w-full btn-primary h-14 text-base"
              onClick={() => navigate("/dashboard")}
              data-testid="done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </main>

      <NavBar active="wallet" />
    </div>
  );
};

export default Exchange;
